import {Injectable} from '@angular/core';
import {ToastController} from '@ionic/angular';
import {AuthService} from 'src/app/shared/services/auth.service';
import {SharedService} from 'src/app/shared/services/shared.service';
import {isEmpty, isNil, orderBy, remove} from 'lodash';
import {JobParams} from 'src/app/shared/modals/job-follow-up/models/jobs';
import {BehaviorSubject, interval, Observable, Subscription} from 'rxjs';
import {JobProgressNotification} from 'src/app/shared/modals/job-follow-up/models/job-progress-notification';
import {JobStatus} from 'src/app/shared/modals/job-follow-up/enums/job-status.enum';
import {distinctUntilChanged, switchMap, filter} from 'rxjs/operators';
import {BackendResponse, BackendService} from 'auth/src/lib/services';
import {LocalStorageService} from 'src/app/shared/services/local-storage.service';
import {environment} from '../../../../environments/environment';
import {BaseClass} from '../../classes/base';
import {TranslationService} from '../../services/translation.service';

@Injectable({providedIn: 'root'})
export class JobFollowUpService extends BaseClass {
    private _pollingSubscriptions: { [key: string]: Subscription } = {};
    private _pollingActive: { [key: string]: boolean } = {};
    private _notifiedJobs: Set<string> = new Set();
    private _autoReactivationSubscription: Subscription;

    private get jobs() {
        return this.jobs$.value
    };

    public jobs$ = new BehaviorSubject<JobParams[]>([]);

    constructor(private authService: AuthService,
                private _sharedService: SharedService,
                private _httpService: BackendService,
                protected _toastController: ToastController,
                protected _translationService: TranslationService,) {

        super(_toastController, _translationService);

        let jobs = this._getJobParams();
        
        jobs = jobs.filter(job => job && job.filters && job.filters['loggedUserId'] === this.authService.getLoggedUserId());
        
        // Marca jobs em estado final como já notificados
        jobs.forEach(job => {
            const { isFinal } = this._isJobInFinalState(job);
            if (isFinal) {
                this._notifiedJobs.add(job.jobId);
                this._pollingActive[job.jobId] = false;
            }
        });
        
        // Ordena por createdAt (se existir) ou jobId como fallback
        this.jobs$.next(jobs.sort((a, b) => {
            const aTime = a.createdAt || Number(a.jobId) || 0;
            const bTime = b.createdAt || Number(b.jobId) || 0;
            return bTime - aTime;
        }));
        this.jobs$.pipe(distinctUntilChanged(), filter(jobParams => jobParams.length > 0)).subscribe((incomingJobs) => {
            localStorage.setItem(LocalStorageService.key.scheduleJobs, JSON.stringify(incomingJobs));
        });

        // Inicia checagem automática a cada 30 segundos para reativar polling de jobs sem polling ativo
        this._startAutoReactivation();
    }


    public getJobsWithoutPolling(): JobParams[] {
        return this.jobs.filter(job => {
            const { isFinal } = this._isJobInFinalState(job);
            return !isFinal && !this._pollingSubscriptions[job.jobId];
        });
    }

    public isPollingActive(jobId: string): boolean {
        return !!this._pollingSubscriptions[jobId];
    }

    public startPollingForJobProgress(service: () => Observable<BackendResponse<JobProgressNotification>>,
                                      job: Partial<JobParams>, currentRetry = 0, pollingInterval = 10000, forceRestart = false) {

        if (isNil(job) || isEmpty(job) || isNil(job.jobId) || isEmpty(job.jobId)) return;
        
        // Se já existe um polling ativo, não cria outro
        if (this._pollingSubscriptions[job.jobId] && !forceRestart) {
            console.log(`Polling already active for jobId: ${job.jobId}`);
            return;
        }
        
        const maxRetries = 3; // Número máximo de tentativas
        if (!job.progress && job.jobId) {
            this._addJob(job as JobParams);
        }
        
        this._pollingActive[job.jobId] = true;

        this._pollingSubscriptions[job.jobId] = interval(pollingInterval).pipe(
            switchMap(() => service())
        ).subscribe((response) => {
            const jobNotification = response.data;
            const jobId = response.data.jobId
            const jobParam = this._getJobById(jobId);
            if (jobNotification && jobParam) {
                jobParam.progress = jobNotification
                this._updateJob(jobParam);
                
                // Verifica se o job está em estado final usando função unificada
                const { isFinal, isSuccess, isFailed } = this._isJobInFinalState(jobParam, response);
                
                if (isFinal) {
                    // Job completado com sucesso e tem dados para download
                    if (isSuccess && jobNotification.data && !jobParam.downloaded) {
                        this._handleSuccessfulJob(jobId, jobNotification, jobParam);
                    } 
                    // Job falhou ou foi deletado
                    else if (isFailed) {
                        console.log(`Job ${jobId} failed or deleted. Stopping polling.`);
                        this._updateJob(jobParam);
                        this._unsubscribePollingSubscription(jobId, false); // Não notifica em caso de falha
                    } 
                    // Job completado mas sem dados ou já baixado
                    else {
                        this._unsubscribePollingSubscription(jobId, false); // Não notifica quando já foi baixado
                    }
                }
            }
        }, error => {
            // Incrementa o retry APENAS quando há erro no endpoint
            const newRetry = currentRetry + 1;
            console.log(`Error on polling for jobId: ${job.jobId}. Retry: ${newRetry}/${maxRetries}`);
            
            const newPollingInterval = 10000 * newRetry; // 10s, 20s, 30s
            console.log(`New Polling Interval: ${newPollingInterval} ms`);
            
            if (newRetry <= maxRetries) {
                // Para o polling atual antes de reiniciar
                if (this._pollingSubscriptions[job.jobId]) {
                    this._pollingSubscriptions[job.jobId].unsubscribe();
                    delete this._pollingSubscriptions[job.jobId];
                }
                
                const jobParam = this._getJobById(job.jobId);
                jobParam ? this.startPollingForJobProgress(service, jobParam, newRetry, newPollingInterval, true)
                    : this.startPollingForJobProgress(service, job, newRetry, newPollingInterval, true);
            } else {
                console.log(`Max retries reached, stopping polling for jobId: ${job.jobId}`);
                
                this._unsubscribePollingSubscription(job.jobId, false); // Não notifica em caso de erro de retry
                const jobParam = this._getJobById(job.jobId);
                if (jobParam && jobParam.progress) {
                    jobParam.progress.status = JobStatus.Fail;
                    jobParam.progress.message = 'generic.jobs.maxRetriesExceeded';
                    this._updateJob(jobParam);
                }
            }
        });
    }

    private _handleSuccessfulJob(jobId: string, jobNotification: any, jobParam: JobParams): void {
        const url = jobNotification.data.replace('/login', '');
        window.open(url, '_blank');
        jobParam.downloaded = true;
        this._updateJob(jobParam);
        this._unsubscribePollingSubscription(jobId);
    }

    private _unsubscribePollingSubscription(jobId: string, shouldNotify: boolean = true): void {
        const subscription = this._pollingSubscriptions[jobId];

        if (subscription) {
            subscription.unsubscribe();
            delete this._pollingSubscriptions[jobId];
            this._pollingActive[jobId] = false; // Marca o polling como inativo permanentemente
            
            // Notifica apenas uma vez e somente se solicitado
            if (shouldNotify && !this._notifiedJobs.has(jobId)) {
                this._notifiedJobs.add(jobId);
                this.notify(this._translationService.getTranslation('Report.SendByEmail'), 'success');
            }
        }
    }

    private _addJob(incomingJob: JobParams) {
        const currentJob = incomingJob.jobId && this.jobs.find(job => job.jobId === incomingJob.jobId);
        if (!currentJob && incomingJob.filters) {
            incomingJob.filters['loggedUserId'] = this.authService.getLoggedUserId();
        }
        if (!currentJob) {
            // Adiciona timestamp de criação
            incomingJob.createdAt = Date.now();
        }
        if (!currentJob && !incomingJob.progress) {
            incomingJob.progress = new JobProgressNotification<string>({
                status: JobStatus.Enqueued,
                message: 'generic.Job.Enqueued',
                progressPercentage: 0,
            })
        }
        const jobs = isNil(currentJob) ? [...this.jobs, incomingJob] : [...this.jobs];
        this.jobs$.next(jobs.sort((a, b) => {
            const aTime = a.createdAt || Number(a.jobId) || 0;
            const bTime = b.createdAt || Number(b.jobId) || 0;
            return bTime - aTime;
        }));
    }

    private _getJobById(jobId: string): JobParams {
        return this.jobs.find(job => job.jobId === jobId);
    }

    private _updateJob(incomingJob: JobParams) {
        const jobs = [...this.jobs];
        const index = jobs.findIndex(job => job.jobId === incomingJob.jobId);
        if (index < 0) {
            throw new Error('generic.job.not.found');
        }
        remove(jobs, (job) => job.jobId === incomingJob.jobId);
        if (incomingJob.filters) {
            incomingJob.filters['loggedUserId'] = this.authService.getLoggedUserId();
        }
        const updatedJobs = [...jobs, incomingJob];
        this.jobs$.next(updatedJobs.sort((a, b) => {
            const aTime = a.createdAt || Number(a.jobId) || 0;
            const bTime = b.createdAt || Number(b.jobId) || 0;
            return bTime - aTime;
        }));
    }

    private _getJobParams() {
        const jsonResponse = localStorage.getItem(LocalStorageService.key.scheduleJobs);
        if (isNil(jsonResponse) || isEmpty(jsonResponse)) return [] as JobParams[];
        return JSON.parse(jsonResponse) as JobParams[];
    }

    public deleteJob(jobId: string): void {
        if (isNil(jobId) || isEmpty(jobId)) return;
        
        // Remove da lista de jobs notificados
        this._notifiedJobs.delete(jobId);
        
        // Para o polling se estiver ativo
        if (this._pollingSubscriptions[jobId]) {
            this._pollingSubscriptions[jobId].unsubscribe();
            delete this._pollingSubscriptions[jobId];
        }
        delete this._pollingActive[jobId];
        
        // Remove da lista de jobs
        const jobs = this.jobs.filter(job => job.jobId !== jobId);
        this.jobs$.next(jobs.sort((a, b) => {
            const aTime = a.createdAt || Number(a.jobId) || 0;
            const bTime = b.createdAt || Number(b.jobId) || 0;
            return bTime - aTime;
        }));
    }

    private _checkResponse(response: BackendResponse<JobProgressNotification>, jobStatus: JobStatus): boolean {
        return (
            response &&
            response.success &&
            response.data.status === jobStatus
        );
    }

    private _isJobInFinalState(jobParam: JobParams, response?: BackendResponse<JobProgressNotification>): { isFinal: boolean; isSuccess: boolean; isFailed: boolean } {
        // Usa response.data.status se disponível, senão usa jobParam.progress.status
        const status = response?.data?.status ?? jobParam.progress?.status;
        
        const isSuccess = status === JobStatus.Completed;
        const isFailed = status === JobStatus.Fail || status === JobStatus.Deleted;
        const isFinal = isSuccess || isFailed;
        
        return { isFinal, isSuccess, isFailed };
    }

    private _startAutoReactivation(): void {
        // Verifica a cada 30 segundos se há jobs que precisam ter o polling reativado
        this._autoReactivationSubscription = interval(30000).subscribe(() => {
            this._removeOldJobs();
            this._reactivateJobsWithoutPolling();
        });
    }

    private _removeOldJobs(): void {
        const currentTime = Date.now();
        const oneDayInMs = 24 * 60 * 60 * 1000; // 24 horas em milissegundos
        
        const jobsToRemove = this.jobs.filter(job => {
            const jobTime = job.createdAt || Number(job.jobId) || 0;
            
            // Se não tem timestamp válido (job criado antes da feature), remove
            if (job.createdAt == null || job.createdAt == undefined || isNaN(job.createdAt)) {
                return true;
            }
            
            const jobAge = currentTime - jobTime;
            return jobAge > oneDayInMs;
        });
        
        if (jobsToRemove.length > 0) {
            console.log(`Removing ${jobsToRemove.length} jobs older than 24 hours`);
            
            jobsToRemove.forEach(job => {
                console.log(`Removing old job: ${job.jobId}, age: ${job.createdAt ? 'with timestamp' : 'without timestamp'}`);
                this.deleteJob(job.jobId);
            });
        }
    }

    private _reactivateJobsWithoutPolling(): void {
        const jobsWithoutPolling = this.getJobsWithoutPolling();
        
        if (jobsWithoutPolling.length === 0) {
            return;
        }
        
        console.log(`Auto-reactivating polling for ${jobsWithoutPolling.length} jobs`);
        
        jobsWithoutPolling.forEach(job => {
            console.log(`Reactivating polling for job: ${job.jobId}`);
            const service = () => this._httpService.get<JobProgressNotification>('getReportJobStatus', [job.jobId], []);
            this.startPollingForJobProgress(service, job, 0, 10000, true);
        });
    }

    public ngOnDestroy(): void {
        // Limpa a subscription de auto-reativação quando o serviço for destruído
        if (this._autoReactivationSubscription) {
            this._autoReactivationSubscription.unsubscribe();
        }
        
        // Limpa todas as polling subscriptions
        Object.keys(this._pollingSubscriptions).forEach(jobId => {
            if (this._pollingSubscriptions[jobId]) {
                this._pollingSubscriptions[jobId].unsubscribe();
            }
        });
    }
}
